import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import { FrontendAgreements } from "../../../hooks/useTermsAgreements.ts";
import { signupService } from "../../../services";
import { STORAGE_KEYS } from "../../../types/storage";
import ErrorMessage from "../../common/ErrorMessage";
import LoadingSpinner from "../../common/LoadingSpinner";

const SignupVerificationCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { login } = useAuth();

  useEffect(() => {
    processVerificationResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processVerificationResult = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // URL에서 resultData 파라미터 추출
      const resultData = searchParams.get("resultData");

      if (!resultData) {
        throw new Error("본인인증 결과 데이터가 없습니다.");
      }

      const identityVerificationId = sessionStorage.getItem(STORAGE_KEYS.VERIFICATION_ID);

      if (!identityVerificationId) {
        throw new Error("본인인증 ID가 없습니다.");
      }

      let userTermsAgreementList: FrontendAgreements

      const userTermsAgreementListString = sessionStorage.getItem(STORAGE_KEYS.USER_TERMS_AGREEMENT_LIST);
      if (!userTermsAgreementListString) {
        throw new Error("약관 동의 정보가 없습니다.");
      }

      try {
        userTermsAgreementList = JSON.parse(userTermsAgreementListString);
      } catch {
        throw new Error("약관 동의 정보가 유효하지 않습니다.");
      }

      // 백엔드에 본인인증 결과 전송
      const response = await signupService.processSignupVerificationResult({
        resultData,
        identityVerificationId,
        userTermsAgreementList,
      });

      // 로그인 처리 (액세스 토큰 및 리프레시 토큰 저장 및 상태 업데이트)
      login(response.accessToken, response.refreshToken);

      // 세션 스토리지 정리
      sessionStorage.removeItem(STORAGE_KEYS.VERIFICATION_ID);
      sessionStorage.removeItem(STORAGE_KEYS.USER_TERMS_AGREEMENT_LIST);

      // 성공 시 데이터 전송요구서 동의 페이지로 이동
      navigate("/auth/data-transfer");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error("본인인증 결과 처리 실패:", error);
      setError(error.message || "본인인증 처리 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [navigate, searchParams, login]);

  const handleGoBack = () => {
    navigate("/");
  };

  if (loading) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-4">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600 text-center">
          본인인증 결과를 처리하고 있습니다...
          <br />
          잠시만 기다려주세요.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen-dvh bg-gray-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">
          <ErrorMessage message={error} />
          <div className="mt-6 text-center">
            <button
              onClick={handleGoBack}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 성공 시에는 이미 navigate 되므로 이 부분이 렌더링되지 않음
  return null;
};

export default SignupVerificationCallback;
